import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { FixedSizeList as List } from 'react-window';
import { GeneralSelect, GeneralInput, LoadingIndicator, InfoMessage, GeneralDialog } from 'sharedComponents';
import { ASSET_TYPES, popupTypes, MAX_INPUT_CHAR_LENGTH } from 'helpers/constants';
import { ASSET_SELECTION_CONSTANTS, ASSET_TYPE_STRINGS, ASSET_STATUS, DIALOG_TYPES, DEFAULT_GENERIC_MACHINE_SUB_TYPES } from './constants';
import AvailableAssetItem from './components/AvailableAssetItem';
import SelectedAssetItem from './components/SelectedAssetItem';
import './style.scss';

/**
 * AssetSelectionStep - Optimized component for selecting powertrain assets
 * Features:
 * - Virtualized lists for performance
 * - Debounced search with cleanup
 * - Memoized computations
 * - Proper API call management
 * - Memory leak prevention
 */
const AssetSelectionStep = ({
    stepData = {},
    allStepData = {},
    onStepDataChange = () => { },
    mode = ASSET_SELECTION_CONSTANTS.CREATE_MODE,
    isActive = false,
    actions = {},
    availableAssets = [],
    isLoadingAssets = false,
    assetsError = null,
    assetSubTypes = [],
    isLoadingAssetSubTypes = false,
    assetSubTypesError = null, // eslint-disable-line no-unused-vars
    preSelectedAssetIds = []
}) => {
    const { t: translate } = useTranslation();
    // Core state with proper initialization
    const [formData, setFormData] = useState(() => ({
        selectedAssets: stepData.selectedAssets || [],
        hasPhysicalConnectionChanges: stepData.hasPhysicalConnectionChanges || false
    }));

    // Debounced search state
    const [searchTerm, setSearchTerm] = useState('');
    const [assetTypeFilter, setAssetTypeFilter] = useState('');

    // Dynamic height state
    const [listHeight, setListHeight] = useState(400);
    const availableContainerRef = useRef(null);
    const selectedContainerRef = useRef(null);

    // Dialog state for edit mode warnings
    const [dialogState, setDialogState] = useState({
        type: null, // 'unselect', 'removeSubType', 'changeSubType'
        show: false,
        asset: null,
        newSubType: null
    });

    // Refs for cleanup and optimization
    const apiCallTracker = useRef(new Set());
    const componentMountedRef = useRef(true);
    const onStepDataChangeRef = useRef(onStepDataChange);
    const initialAssetsRef = useRef(null);
    const hasAutoSelectedRef = useRef(false);

    // Store initial assets in edit mode (only once on mount)
    useEffect(() => {
        if (mode === ASSET_SELECTION_CONSTANTS.EDIT_MODE && !initialAssetsRef.current && stepData.selectedAssets?.length > 0) {
            initialAssetsRef.current = stepData.selectedAssets.map(asset => ({
                assetId: asset.assetId,
                selectedSubType: asset.selectedSubType,
                isConnected: asset.isConnected
            }));
        }
    }, [mode]); // Only run when mode is available

    // Keep the callback ref updated
    useEffect(() => {
        onStepDataChangeRef.current = onStepDataChange;
    });

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            componentMountedRef.current = false;
        };
    }, []);

    // Calculate list height dynamically based on container
    useEffect(() => {
        const calculateHeight = () => {
            const container = availableContainerRef.current || selectedContainerRef.current;
            if (container) {
                const containerHeight = container.offsetHeight;
                if (containerHeight > 0) {
                    setListHeight(containerHeight);
                }
            }
        };

        calculateHeight();
        window.addEventListener('resize', calculateHeight);

        return () => {
            window.removeEventListener('resize', calculateHeight);
        };
    }, [isActive]);

    // Optimized asset subtypes API call with proper tracking
    const makeAssetSubTypesCall = useCallback((typeConfigId) => {
        if (!componentMountedRef.current || apiCallTracker.current.has(typeConfigId)) {
            return;
        }

        if (assetSubTypes?.length > 0) {
            return;
        }

        apiCallTracker.current.add(typeConfigId);

        if (actions?.getAssetSubTypesRequest) {
            actions.getAssetSubTypesRequest(typeConfigId);
        }
    }, []); // Zero dependencies for stable reference

    // Reset tracker when step becomes inactive
    useEffect(() => {
        if (!isActive) {
            apiCallTracker.current.clear();
        }
    }, [isActive]);

    // Request asset subtypes for GenericMachine assets
    useEffect(() => {
        if (!isActive || !formData?.selectedAssets?.length || !componentMountedRef.current) {
            return;
        }

        const genericMachineAsset = formData.selectedAssets.find(asset => {
            const isGenericMachine = asset.assetType === ASSET_TYPE_STRINGS.GENERIC_MACHINE || asset.assetType === ASSET_TYPES.GENERICMACHINE;
            const hasTypeConfigId = asset.typeConfigId || asset.assetTypeConfigId || asset.configId;
            return isGenericMachine && hasTypeConfigId;
        });

        if (genericMachineAsset) {
            const typeConfigId = genericMachineAsset.typeConfigId ||
                genericMachineAsset.assetTypeConfigId ||
                genericMachineAsset.configId;
            makeAssetSubTypesCall(typeConfigId);
        }
    }, [isActive, formData?.selectedAssets, makeAssetSubTypesCall]);

    // Memoized subtype options for better performance
    const getSubTypeOptions = useCallback((assetType, providedTypeConfigId) => {
        if (assetType !== ASSET_TYPE_STRINGS.GENERIC_MACHINE && assetType !== ASSET_TYPES.GENERICMACHINE) {
            return [];
        }

        if (!providedTypeConfigId) {
            return DEFAULT_GENERIC_MACHINE_SUB_TYPES;
        }

        if (assetSubTypes && assetSubTypes?.length > 0) {
            return assetSubTypes.map(subtype => {
                if (!subtype || typeof subtype !== 'object') {
                    return null;
                }

                const value = subtype.value || subtype.id || subtype.name || subtype.displayName || 'Unknown';
                const label = subtype.label || subtype.name || subtype.displayName || value;
                const type = subtype.type || subtype.name || subtype.displayName || value;

                return {
                    value: String(value),
                    label: String(label),
                    type: String(type)
                };
            }).filter(Boolean);
        }

        return DEFAULT_GENERIC_MACHINE_SUB_TYPES;
    }, [assetSubTypes]);

    // Selected site from basic info step
    const selectedSite = useMemo(() => {
        return allStepData?.basicInfo?.selectedSite || null;
    }, [allStepData]);

    // Track site changes to detect stale data from a different site
    const prevSiteIdRef = useRef(null);

    // Load available assets EVERY TIME step becomes active or site changes.
    // No caching — always fetch fresh data to avoid stale/cross-site assets.
    useEffect(() => {
        const siteId = selectedSite?.siteId;
        if (!isActive || !siteId || !actions?.getAvailableAssetsRequest || !componentMountedRef.current) {
            return;
        }

        // Detect site change — clear auto-selection flag so pre-selection can re-run
        if (prevSiteIdRef.current !== null && prevSiteIdRef.current !== siteId) {
            hasAutoSelectedRef.current = false;
        }
        prevSiteIdRef.current = siteId;

        const payload = {
            pageNumber: ASSET_SELECTION_CONSTANTS.DEFAULT_PAGE_NUMBER,
            pageSize: ASSET_SELECTION_CONSTANTS.PAGE_SIZE,
            siteId: siteId
        };
        actions.getAvailableAssetsRequest(payload.siteId, payload.pageNumber, payload.pageSize);
    }, [isActive, selectedSite, actions]);

    // Auto-select assets from hierarchy selection once available assets are loaded
    useEffect(() => {
        if (
            !hasAutoSelectedRef.current &&
            preSelectedAssetIds?.length > 0 &&
            availableAssets?.length > 0 &&
            isActive &&
            componentMountedRef.current
        ) {
            const preSelectedIdSet = new Set(preSelectedAssetIds.map(Number));
            const matchingAssets = availableAssets.filter(asset =>
                preSelectedIdSet.has(Number(asset?.assetId))
            );

            if (matchingAssets?.length > 0) {
                const assetsToAdd = matchingAssets.map(asset => ({
                    ...asset,
                    status: ASSET_STATUS.SELECTED,
                    selectedSubType: asset.subType || null,
                    outputSpeedRatio: asset.speedRatio || asset.outputSpeedRatio || null,
                    speedRatio: asset.speedRatio || null
                }));

                setFormData(prev => ({
                    ...prev,
                    selectedAssets: assetsToAdd
                }));
                hasAutoSelectedRef.current = true;
            }
        }
    }, [isActive, availableAssets, preSelectedAssetIds]);

    // Memoized asset type options for filtering
    const assetTypeOptions = useMemo(() => {
        if (!availableAssets || availableAssets?.length === 0) {
            return [{ value: '', label: translate('ABB.Powertrain.Frontend.ptToolsPowertrainWizard_AssetSelection_AllAssetTypesLabel') }];
        }

        const types = [...new Set(availableAssets.map(asset => asset?.assetType))];
        return [
            { value: '', label: translate('ABB.Powertrain.Frontend.ptToolsPowertrainWizard_AssetSelection_AllAssetTypesLabel') },
            ...types.map(type => ({
                value: type,
                label: translate(`ABB.Powertrain.Frontend.${type?.toLowerCase()}Label`) || type
            }))
        ];
    }, [availableAssets, translate]);

    // Filtered and searched assets
    const filteredAvailableAssets = useMemo(() => {
        if (!availableAssets || availableAssets?.length === 0) {
            return [];
        }

        return availableAssets.filter(asset => {
            const matchesSearch = !searchTerm ||
                asset.assetName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                asset.assetType?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                asset.subType?.toLowerCase().includes(searchTerm.toLowerCase());
            const matchesType = !assetTypeFilter || asset.assetType === assetTypeFilter;

            return matchesSearch && matchesType;
        });
    }, [availableAssets, searchTerm, assetTypeFilter]);

    const disabledAvailableAssetIds = useMemo(() => {
        if (!Array.isArray(availableAssets) || availableAssets?.length === 0) {
            return new Set();
        }

        const toIdString = (value) => {
            if (value === null || value === undefined || value === '') {
                return null;
            }
            return String(value);
        };

        const disabledIds = new Set();

        availableAssets?.forEach((asset) => {
            const assetId = toIdString(asset?.assetId);
            const drivingAssetId = toIdString(asset?.drivingAssetId);
            const drivingMotorId = toIdString(asset?.drivingMotorId);

            if (drivingAssetId || drivingMotorId) {
                if (assetId) {
                    disabledIds.add(assetId);
                }
                if (drivingAssetId) {
                    disabledIds.add(drivingAssetId);
                }
                if (drivingMotorId) {
                    disabledIds.add(drivingMotorId);
                }
            }
        });

        return disabledIds;
    }, [availableAssets]);

    // Update parent component with form data changes (with debounce to prevent loops)
    useEffect(() => {
        if (componentMountedRef.current && onStepDataChangeRef.current) {
            onStepDataChangeRef.current(formData);
        }
    }, [formData]); // Removed onStepDataChange from dependencies to prevent infinite loop

    // Sync with external stepData changes
    useEffect(() => {
        if (stepData && stepData.selectedAssets && componentMountedRef.current) {
            // Only update if the arrays are actually different to prevent loops
            const currentLength = formData.selectedAssets?.length || 0;
            const newLength = stepData.selectedAssets?.length || 0;

            if (currentLength !== newLength) {
                setFormData({
                    selectedAssets: stepData.selectedAssets
                });
            } else if (currentLength > 0) {
                // Check if asset IDs are different
                const currentAssetIds = formData.selectedAssets.map(a => a.assetId).sort().join(',');
                const newAssetIds = stepData.selectedAssets.map(a => a.assetId).sort().join(',');

                if (currentAssetIds !== newAssetIds) {
                    setFormData({
                        selectedAssets: stepData.selectedAssets
                    });
                }
            }
        }
    }, [stepData.selectedAssets]);

    // Helper: Check if asset was in the original powertrain (edit mode only)
    const isOriginalAsset = useCallback((assetId) => {
        if (mode !== ASSET_SELECTION_CONSTANTS.EDIT_MODE || !initialAssetsRef.current) {
            return false;
        }
        return initialAssetsRef.current.some(a => a.assetId === assetId);
    }, [mode]);

    // Helper: Check if asset is part of physical connection
    const isPartOfPhysicalConnection = useCallback((assetId) => {
        if (mode !== ASSET_SELECTION_CONSTANTS.EDIT_MODE || !initialAssetsRef.current) {
            return false;
        }
        const initialAsset = initialAssetsRef.current.find(a => a.assetId === assetId);
        return initialAsset?.isConnected === true;
    }, [mode]);

    // Helper: Get original subtype for an asset
    const getOriginalSubType = useCallback((assetId) => {
        if (mode !== ASSET_SELECTION_CONSTANTS.EDIT_MODE || !initialAssetsRef.current) {
            return null;
        }
        const initialAsset = initialAssetsRef.current.find(a => a.assetId === assetId);
        return initialAsset?.selectedSubType || null;
    }, [mode]);

    // Dialog close handler
    const closeDialog = useCallback(() => {
        setDialogState({
            type: null,
            show: false,
            asset: null,
            newSubType: null
        });
    }, []);

    // Confirm unselect asset
    const confirmUnselectAsset = useCallback(() => {
        if (dialogState?.asset) {
            setFormData(prev => ({
                ...prev,
                selectedAssets: prev?.selectedAssets.filter(a => a.assetId !== dialogState?.asset?.assetId)
            }));
        }
        closeDialog();
    }, [dialogState?.asset, closeDialog]);

    // Confirm remove subtype
    const confirmRemoveSubType = useCallback(() => {
        if (dialogState?.asset) {
            setFormData(prev => ({
                ...prev,
                selectedAssets: prev.selectedAssets.map(selectedAsset =>
                    selectedAsset?.assetId === dialogState?.asset?.assetId
                        ? { ...selectedAsset, selectedSubType: null }
                        : selectedAsset
                )
            }));
        }
        closeDialog();
    }, [dialogState?.asset, closeDialog]);

    // Confirm change subtype
    const confirmChangeSubType = useCallback(() => {
        if (dialogState?.asset && dialogState?.newSubType !== null) {
            setFormData(prev => ({
                ...prev,
                selectedAssets: prev.selectedAssets.map(selectedAsset =>
                    selectedAsset?.assetId === dialogState?.asset?.assetId
                        ? { ...selectedAsset, selectedSubType: dialogState?.newSubType }
                        : selectedAsset
                )
            }));
        }
        closeDialog();
    }, [dialogState?.asset, dialogState?.newSubType, closeDialog]);

    const shouldWarnOnUnselect = useCallback((assetId) => {
        return mode === ASSET_SELECTION_CONSTANTS.EDIT_MODE && isOriginalAsset(assetId) && isPartOfPhysicalConnection(assetId);
    }, [mode, isOriginalAsset, isPartOfPhysicalConnection]);

    const showUnselectDialog = useCallback((asset) => {
        setDialogState({
            type: DIALOG_TYPES.UNSELECT,
            show: true,
            asset: asset,
            newSubType: null
        });
    }, []);

    const removeSelectedAsset = useCallback((assetId) => {
        setFormData(prev => ({
            ...prev,
            selectedAssets: prev.selectedAssets.filter(a => a.assetId !== assetId)
        }));
    }, []);

    const requestGenericMachineSubTypes = useCallback((asset) => {
        const isGenericMachine = asset?.assetType === ASSET_TYPE_STRINGS.GENERIC_MACHINE || asset?.assetType === ASSET_TYPES.GENERICMACHINE;
        if (!isGenericMachine || !actions?.getAssetSubTypesRequest || isLoadingAssetSubTypes) {
            return;
        }

        const typeConfigId = asset.typeConfigId || asset.assetTypeConfigId || asset.configId;
        if (typeConfigId && componentMountedRef.current) {
            actions.getAssetSubTypesRequest(typeConfigId);
        }
    }, [actions, isLoadingAssetSubTypes]);

    const addSelectedAsset = useCallback((asset) => {
        const assetToAdd = {
            ...asset,
            status: ASSET_STATUS.SELECTED,
            selectedSubType: asset.subType || null,
            outputSpeedRatio: asset.speedRatio || asset.outputSpeedRatio || null,
            speedRatio: asset.speedRatio || null
        };

        setFormData(prev => ({
            ...prev,
            selectedAssets: [...prev.selectedAssets, assetToAdd]
        }));

        requestGenericMachineSubTypes(asset);
    }, [requestGenericMachineSubTypes]);

    // Optimized asset toggle function with edit mode check
    const toggleAsset = useCallback((asset) => {
        const isAlreadySelected = formData.selectedAssets.some(selectedAsset =>
            selectedAsset.assetId === asset.assetId
        );

        const currentAssetId = asset?.assetId !== null && asset?.assetId !== undefined ? String(asset.assetId) : null;
        const isSelectionDisabled = !isAlreadySelected && currentAssetId && disabledAvailableAssetIds.has(currentAssetId);

        if (isSelectionDisabled) {
            return;
        }

        if (isAlreadySelected) {
            if (shouldWarnOnUnselect(asset.assetId)) {
                showUnselectDialog(asset);
                return;
            }

            removeSelectedAsset(asset.assetId);
            return;
        }

        addSelectedAsset(asset);
    }, [formData.selectedAssets, disabledAvailableAssetIds, shouldWarnOnUnselect, showUnselectDialog, removeSelectedAsset, addSelectedAsset]);

    const removeAsset = useCallback((asset) => {
        if (shouldWarnOnUnselect(asset?.assetId)) {
            showUnselectDialog(asset);
            return;
        }

        removeSelectedAsset(asset?.assetId);
    }, [shouldWarnOnUnselect, showUnselectDialog, removeSelectedAsset]);

    const handleSubTypeChange = useCallback((asset, subType) => {
        // In edit mode, check if this is a change to an existing asset's subtype that's part of physical connection
        if (mode === ASSET_SELECTION_CONSTANTS.EDIT_MODE && isOriginalAsset(asset?.assetId) && isPartOfPhysicalConnection(asset?.assetId)) {
            const originalSubType = getOriginalSubType(asset?.assetId);
            // If changing from an existing subtype, show warning
            if (originalSubType && originalSubType !== subType) {
                setDialogState({
                    type: DIALOG_TYPES.CHANGE_SUB_TYPE,
                    show: true,
                    asset: asset,
                    newSubType: subType
                });
                return;
            }
        }

        // Directly apply the change
        setFormData(prev => ({
            ...prev,
            selectedAssets: prev.selectedAssets.map(selectedAsset =>
                selectedAsset?.assetId === asset?.assetId
                    ? { ...selectedAsset, selectedSubType: subType }
                    : selectedAsset
            )
        }));
    }, [mode, isOriginalAsset, isPartOfPhysicalConnection, getOriginalSubType]);

    const handleSubTypeClear = useCallback((asset) => {
        // In edit mode, check if this is clearing an existing subtype that's part of physical connection
        if (mode === ASSET_SELECTION_CONSTANTS.EDIT_MODE && isOriginalAsset(asset?.assetId) && isPartOfPhysicalConnection(asset?.assetId)) {
            const originalSubType = getOriginalSubType(asset?.assetId);
            // If clearing an existing subtype, show warning
            if (originalSubType) {
                setDialogState({
                    type: DIALOG_TYPES.REMOVE_SUB_TYPE,
                    show: true,
                    asset: asset,
                    newSubType: null
                });
                return;
            }
        }

        // Directly clear the subtype
        setFormData(prev => ({
            ...prev,
            selectedAssets: prev.selectedAssets.map(selectedAsset =>
                selectedAsset?.assetId === asset?.assetId
                    ? { ...selectedAsset, selectedSubType: null }
                    : selectedAsset
            )
        }));
    }, [mode, isOriginalAsset, isPartOfPhysicalConnection, getOriginalSubType]);

    const handleSearchChange = useCallback((e) => {
        const value = e?.target?.value || e?.value || e || '';
        setSearchTerm(value);
    }, []);

    const handleTypeFilterChange = useCallback((selectedOption) => {
        const value = selectedOption?.value ?? '';
        setAssetTypeFilter(value);
    }, []);

    // Asset item component for available assets list
    const AvailableAssetItemRenderer = useCallback(({ index, style }) => {
        const asset = filteredAvailableAssets[index];
        const isSelected = formData.selectedAssets.some(selectedAsset =>
            selectedAsset?.assetId === asset?.assetId
        );
        const assetId = asset?.assetId !== null && asset?.assetId !== undefined ? String(asset.assetId) : null;
        const isDisabled = assetId ? disabledAvailableAssetIds.has(assetId) : false;

        return (
            <AvailableAssetItem
                style={style}
                asset={asset}
                isSelected={isSelected}
                isDisabled={isDisabled}
                disabledReason={isDisabled ? translate('ABB.Powertrain.Frontend.ptToolsPowertrainWizard_Validation_AssetAlreadyConnected') : ''}
                onToggle={toggleAsset}
            />
        );
    }, [filteredAvailableAssets, toggleAsset, formData.selectedAssets, disabledAvailableAssetIds]);

    // Asset item component for selected assets list
    const SelectedAssetItemRenderer = useCallback(({ index, style }) => {
        const asset = formData.selectedAssets[index];
        const typeConfigId = asset?.typeConfigId || asset?.assetTypeConfigId || asset?.configId;
        const subTypeOptions = getSubTypeOptions(asset?.assetType, typeConfigId);
        const selectedSubType = asset?.selectedSubType; // Get subtype directly from asset

        return (
            <SelectedAssetItem
                style={style}
                asset={asset}
                subTypeOptions={subTypeOptions}
                selectedSubType={selectedSubType}
                onSubTypeChange={handleSubTypeChange}
                onSubTypeClear={handleSubTypeClear}
                onRemove={removeAsset}
            />
        );
    }, [formData.selectedAssets, getSubTypeOptions, handleSubTypeChange, handleSubTypeClear, removeAsset]);

    // Show error state if there's an error
    if (assetsError && !isLoadingAssets) {
        return (
            <div className={`asset-selection-step ${isActive ? 'active' : ''}`}>
                <InfoMessage
                    title={translate('ABB.Powertrain.Frontend.ptToolsPowertrainWizard_AssetSelection_ErrorLoadingAssetsTitle')}
                    message={assetsError.message || translate('ABB.Powertrain.Frontend.ptToolsPowertrainWizard_AssetSelection_ErrorLoadingAssetsMessage')}
                    type='error'
                />
            </div>
        );
    }

    // Render warning dialogs for edit mode
    const renderWarningDialogs = () => {
        if (dialogState.type === DIALOG_TYPES.UNSELECT) {
            return (
                <GeneralDialog
                    show={dialogState.show}
                    close={closeDialog}
                    title={translate('ABB.Powertrain.Frontend.ptToolsPowertrainWizard_AssetSelection_UnselectAssetDialog_Title')}
                    acceptButtonProps={{
                        text: translate('ABB.Powertrain.Frontend.ptToolsPowertrainWizard_AssetSelection_UnselectAssetDialog_UnselectButton'),
                        onClick: confirmUnselectAsset
                    }}
                    cancelButtonProps={{
                        text: translate('ABB.Powertrain.Frontend.ptToolsPowertrainWizard_AssetSelection_UnselectAssetDialog_CancelButton'),
                        onClick: closeDialog
                    }}
                    notificationType={popupTypes.WARNING}
                >
                    <>{translate('ABB.Powertrain.Frontend.ptToolsPowertrainWizard_AssetSelection_UnselectAssetDialog_Body')}</>
                </GeneralDialog>
            );
        }

        if (dialogState.type === DIALOG_TYPES.REMOVE_SUB_TYPE) {
            return (
                <GeneralDialog
                    show={dialogState.show}
                    close={closeDialog}
                    title={translate('ABB.Powertrain.Frontend.ptToolsPowertrainWizard_AssetSelection_RemoveSubTypeDialog_Title')}
                    acceptButtonProps={{
                        text: translate('ABB.Powertrain.Frontend.ptToolsPowertrainWizard_AssetSelection_RemoveSubTypeDialog_RemoveButton'),
                        onClick: confirmRemoveSubType
                    }}
                    cancelButtonProps={{
                        text: translate('ABB.Powertrain.Frontend.ptToolsPowertrainWizard_AssetSelection_RemoveSubTypeDialog_CancelButton'),
                        onClick: closeDialog
                    }}
                    notificationType={popupTypes.WARNING}
                >
                    <>{translate('ABB.Powertrain.Frontend.ptToolsPowertrainWizard_AssetSelection_RemoveSubTypeDialog_Body')}</>
                </GeneralDialog>
            );
        }

        if (dialogState.type === DIALOG_TYPES.CHANGE_SUB_TYPE) {
            const bodyText = translate('ABB.Powertrain.Frontend.ptToolsPowertrainWizard_AssetSelection_ChangeSubTypeDialog_Body')
                .replace('{newType}', dialogState?.newSubType || '');

            return (
                <GeneralDialog
                    show={dialogState.show}
                    close={closeDialog}
                    title={translate('ABB.Powertrain.Frontend.ptToolsPowertrainWizard_AssetSelection_ChangeSubTypeDialog_Title')}
                    acceptButtonProps={{
                        text: translate('ABB.Powertrain.Frontend.ptToolsPowertrainWizard_AssetSelection_ChangeSubTypeDialog_ChangeButton'),
                        onClick: confirmChangeSubType
                    }}
                    cancelButtonProps={{
                        text: translate('ABB.Powertrain.Frontend.ptToolsPowertrainWizard_AssetSelection_ChangeSubTypeDialog_CancelButton'),
                        onClick: closeDialog
                    }}
                    notificationType={popupTypes.WARNING}
                >
                    <>{bodyText}</>
                </GeneralDialog>
            );
        }

        return null;
    };

    // Show message if no site is selected
    if (!selectedSite) {
        return (
            <div className={`asset-selection-step ${isActive ? 'active' : ''}`}>
                <InfoMessage
                    title={translate('ABB.Powertrain.Frontend.ptToolsPowertrainWizard_AssetSelection_SiteRequiredTitle')}
                    message={translate('ABB.Powertrain.Frontend.ptToolsPowertrainWizard_AssetSelection_SiteRequiredMessage')}
                    type='info'
                />
            </div>
        );
    }

    // Show spinner only on the first visit (no assets loaded yet).
    // On subsequent step revisits, assets display immediately while a background refresh runs.
    const showLeftPanelSpinner = isLoadingAssets && availableAssets?.length === 0;

    // Show "no assets" message only after loading has completed
    const apiHasResolved = !isLoadingAssets;

    const renderAvailableAssetsContent = () => {
        if (showLeftPanelSpinner) {
            return (
                <div className='panel-loading-state'>
                    <LoadingIndicator size='medium' />
                </div>
            );
        }
        if (filteredAvailableAssets?.length > 0) {
            return (
                <List
                    height={listHeight}
                    itemCount={filteredAvailableAssets?.length}
                    itemSize={52}
                    className='assets-virtualized-list'
                    width='100%'
                >
                    {AvailableAssetItemRenderer}
                </List>
            );
        }
        if (apiHasResolved) {
            return (
                <div className='empty-state'>
                    <p>{translate('ABB.Powertrain.Frontend.ptToolsPowertrainWizard_AssetSelection_NoAssetsFoundMessage')}</p>
                </div>
            );
        }
        return null;
    };

    return (
        <div className={`asset-selection-step ${isActive ? 'active' : ''}`}>
            <div className='asset-selection-container'>
                {/* Available Assets Panel */}
                <div className='assets-panel available-panel'>
                    <div className='panel-header'>
                        <h3 className='panel-title'>{translate('ABB.Powertrain.Frontend.ptToolsPowertrainWizard_AssetSelection_AvailableAssetsTitle')}</h3>
                        <div className='panel-controls'>
                            <div className='control-row'>
                                <div className='asset-type-filter'>
                                    <GeneralSelect
                                        className='filter-select'
                                        placeholder={translate('ABB.Powertrain.Frontend.ptToolsPowertrainWizard_AssetSelection_AssetTypePlaceholder')}
                                        value={assetTypeOptions.find(opt => opt.value === assetTypeFilter)}
                                        onChange={handleTypeFilterChange}
                                        options={assetTypeOptions}
                                    />
                                </div>
                                <div className='search-container'>
                                    <GeneralInput
                                        className='search-input'
                                        placeholder={translate('ABB.Powertrain.Frontend.ptToolsPowertrainWizard_AssetSelection_SearchPlaceholder')}
                                        value={searchTerm}
                                        onChange={handleSearchChange}
                                        maxLength={MAX_INPUT_CHAR_LENGTH}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className='panel-content'>
                        <div className='assets-list-container' ref={availableContainerRef}>
                            {renderAvailableAssetsContent()}
                        </div>
                    </div>
                </div>

                {/* Selected Assets Panel */}
                <div className='assets-panel selected-panel'>
                    <div className='panel-header'>
                        <h3 className='panel-title'>{translate('ABB.Powertrain.Frontend.ptToolsPowertrainWizard_AssetSelection_SelectedAssetsTitle')}</h3>
                        <div className='selected-assets-header'>
                            <div className='header-cell asset-name-header'>{translate('ABB.Powertrain.Frontend.ptToolsPowertrainWizard_AssetSelection_AssetNameHeader')}</div>
                            <div className='header-cell sub-type-header'>{translate('ABB.Powertrain.Frontend.ptToolsPowertrainWizard_AssetSelection_SubTypeHeader')}</div>
                            <div className='header-cell actions-header'></div>
                        </div>
                    </div>
                    <div className='panel-content'>
                        <div className='assets-list-container' ref={selectedContainerRef}>
                            {formData?.selectedAssets?.length > 0 ?
                                <List
                                    height={listHeight}
                                    itemCount={formData?.selectedAssets?.length}
                                    itemSize={52}
                                    className='assets-virtualized-list'
                                    width='100%'
                                >
                                    {SelectedAssetItemRenderer}
                                </List>
                                :
                                <div className='empty-state'>
                                    <p>{translate('ABB.Powertrain.Frontend.ptToolsPowertrainWizard_AssetSelection_NoAssetsSelectedMessage')}</p>
                                    <p>{translate('ABB.Powertrain.Frontend.ptToolsPowertrainWizard_AssetSelection_NoAssetsSelectedSubMessage')}</p>
                                </div>
                            }
                        </div>
                    </div>
                </div>
            </div>
            {/* Warning dialogs for edit mode */}
            {renderWarningDialogs()}
        </div>
    );
};

AssetSelectionStep.propTypes = {
    stepData: PropTypes.object,
    allStepData: PropTypes.object,
    onStepDataChange: PropTypes.func,
    mode: PropTypes.oneOf([ASSET_SELECTION_CONSTANTS.CREATE_MODE, ASSET_SELECTION_CONSTANTS.EDIT_MODE]),
    isActive: PropTypes.bool,
    // Actions passed from PowertrainWizardComponentV2
    actions: PropTypes.shape({
        getAvailableAssetsRequest: PropTypes.func,
        getAssetSubTypesRequest: PropTypes.func,
    }),
    // State props (will be managed by wizard state)
    availableAssets: PropTypes.array,
    isLoadingAssets: PropTypes.bool,
    assetsError: PropTypes.object,
    // Asset subtypes state
    assetSubTypes: PropTypes.array,
    isLoadingAssetSubTypes: PropTypes.bool,
    assetSubTypesError: PropTypes.object,
    preSelectedAssetIds: PropTypes.array,
};

export default AssetSelectionStep;
